import { getLocale, useLocale, type Locale } from './locale'

type Message = string | ((...args: never[]) => string)

export type MessageShape<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => string ? (...args: A) => string : string
}

export type MessageDict<T> = Record<Locale, MessageShape<T>>

export function defineMessages<T extends Record<string, Message>>(
  is: T,
  translations: { en: MessageShape<T>; pl: MessageShape<T> }
): MessageDict<T> {
  return { is: is as unknown as MessageShape<T>, en: translations.en, pl: translations.pl }
}

export function useMessages<T>(dict: MessageDict<T>): MessageShape<T> {
  return dict[useLocale()]
}

export function messagesFor<T>(dict: MessageDict<T>, locale: Locale = getLocale()): MessageShape<T> {
  return dict[locale]
}

export function pluralIs(n: number, one: string, other: string): string {
  const i = Math.abs(Math.trunc(n))
  return Number.isInteger(n) && i % 10 === 1 && i % 100 !== 11 ? one : other
}

export function pluralEn(n: number, one: string, other: string): string {
  return n === 1 ? one : other
}

export function pluralPl(n: number, one: string, few: string, many: string): string {
  if (!Number.isInteger(n)) return few
  const i = Math.abs(n)
  if (i === 1) return one
  const last = i % 10
  const lastTwo = i % 100
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? few : many
}
